/**
 * Presentation only. Nothing here computes with money.
 *
 * Amounts arrive as decimal strings and are grouped for reading without
 * being parsed into a float, because `Number("11776469.00")` is a lossy
 * conversion performed for cosmetic reasons - the worst kind of bug to
 * introduce, since it looks like formatting code.
 */

export function groupDigits(amount: string | null): string {
  if (amount === null || amount === "") return "";
  const negative = amount.startsWith("-");
  const bare = negative ? amount.slice(1) : amount;
  const [whole = "", fraction] = bare.split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  const rendered = fraction ? `${grouped}.${fraction}` : grouped;
  return negative ? `-${rendered}` : rendered;
}

export function money(amount: string | null, currency: string | null): string {
  if (amount === null) return "";
  return currency ? `${groupDigits(amount)} ${currency}` : groupDigits(amount);
}

/**
 * A proportion as a percentage: "0.19" reads as "19%".
 *
 * The decimal point is moved by shifting digits rather than by
 * multiplying by 100, because this project's whole argument is that
 * money arithmetic does not happen in floating point, and a display
 * function is exactly where that gets forgotten.
 *
 * A rate of "19" - the classic model mistake, 19 written where 0.19 was
 * meant - therefore renders as "1900%" rather than being quietly
 * reinterpreted. It should look wrong, because it is.
 */
export function ratePercent(rate: string | null): string {
  if (rate === null) return "";
  const negative = rate.startsWith("-");
  const bare = negative ? rate.slice(1) : rate;
  const [rawWhole = "0", rawFraction = ""] = bare.split(".");

  const padded = rawFraction.padEnd(2, "0");
  const whole = (rawWhole + padded.slice(0, 2)).replace(/^0+(?=\d)/, "");
  const fraction = padded.slice(2).replace(/0+$/, "");

  const rendered = fraction ? `${whole}.${fraction}` : whole;
  return `${negative ? "-" : ""}${rendered}%`;
}

export function shortDate(value: string | null): string {
  if (!value) return "";
  return value.slice(0, 10);
}

export function timestamp(value: string): string {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toISOString().replace("T", " ").slice(0, 16);
}

export function bytes(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} kB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}
